import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { toast } from 'sonner';
import { CheckCircle, ArrowLeft, UploadSimple, File as FileIcon, X } from '@phosphor-icons/react';

const BulkInquiry = () => {
  const [searchParams] = useSearchParams();
  const productIdParam = searchParams.get('product');
  const [mode, setMode] = useState(productIdParam ? 'existing' : null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [existingForm, setExistingForm] = useState({
    name: '', email: '', phone: '', quantity: 10, color: '', finish: 'Matte', message: ''
  });

  const finishOptions = ['Glossy', 'Matte', 'Grainy'];

  const [ownForm, setOwnForm] = useState({
    name: '', email: '', phone: '', quantity: 10, requirements: ''
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (productIdParam && products.length > 0) {
      const p = products.find(pr => pr.id === productIdParam);
      if (p) setSelectedProduct(p);
    }
  }, [productIdParam, products]);

  // When a product is selected, default the color to its first available swatch
  useEffect(() => {
    if (selectedProduct?.colors?.length > 0) {
      setExistingForm(prev => ({ ...prev, color: prev.color || selectedProduct.colors[0] }));
    }
  }, [selectedProduct]);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'pdf'].includes(ext)) {
      toast.error('Only JPEG, PNG, and PDF files are allowed');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadedFile({ path: response.data.path, filename: file.name });
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleExistingSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Prepend colour & finish selections into the admin-visible message
      const selectionSummary = `Color: ${existingForm.color || 'Not specified'} · Finish: ${existingForm.finish}`;
      const finalMessage = existingForm.message
        ? `${selectionSummary}\n\n${existingForm.message}`
        : selectionSummary;
      await api.post('/inquiries/bulk', {
        name: existingForm.name,
        email: existingForm.email,
        phone: existingForm.phone,
        product_id: selectedProduct.id,
        quantity: parseInt(existingForm.quantity),
        message: finalMessage
      });
      setSubmitted(true);
      toast.success('Bulk inquiry submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit inquiry');
    } finally {
      setLoading(false);
    }
  };

  const handleOwnSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const requirements = uploadedFile
        ? `${ownForm.requirements}\n\nDesign file: ${uploadedFile.filename} (${uploadedFile.path})`
        : ownForm.requirements;
      await api.post('/inquiries/bulk', {
        name: ownForm.name,
        email: ownForm.email,
        phone: ownForm.phone,
        product_id: 'custom-design',
        quantity: parseInt(ownForm.quantity),
        message: requirements
      });
      setSubmitted(true);
      toast.success('Bulk inquiry submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit inquiry');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="bulk-inquiry-success" className="min-h-screen pt-24 pb-16 bg-[#F4F4F4] flex items-center justify-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md px-6">
          <CheckCircle size={64} weight="thin" className="text-green-600 mx-auto mb-4" />
          <h2 className="text-3xl font-light mb-4">Thank You!</h2>
          <p className="text-[#7a5e75] mb-8">Your bulk inquiry has been received. Our sales team will contact you with a special quote within 24 hours.</p>
          <button onClick={() => { setSubmitted(false); setMode(null); setSelectedProduct(null); setUploadedFile(null); }} data-testid="submit-another-bulk-inquiry-button" className="btn-secondary">
            Submit Another Inquiry
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="bulk-inquiry-page" className="min-h-screen pt-24 pb-16 bg-[#F4F4F4]">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
          <h1 data-testid="bulk-inquiry-heading" className="text-4xl md:text-5xl font-light tracking-tighter mb-4">
            Bulk Order Inquiry
          </h1>
          <p className="text-base text-[#7a5e75] leading-relaxed">
            Looking to order in bulk? Get special pricing for large quantity orders. Minimum 10 units.
          </p>
        </motion.div>

        {/* Step 1: Choose mode */}
        {!mode && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <button
              data-testid="bulk-existing-button"
              onClick={() => setMode('existing')}
              className="group text-left bg-white border border-[#E5E2DC] overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
            >
              <div className="h-80 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/2w28ryv4_ChatGPT%20Image%20Apr%2018%2C%202026%20at%2011_27_12%20AM.png"
                  alt="Order our existing designs"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-light text-[#641b4d] mb-2">Order Our Existing Design</h3>
                <p className="text-sm text-[#7a5e75]">Select from our existing collection and place a bulk order with special pricing.</p>
              </div>
            </button>

            <button
              data-testid="bulk-own-button"
              onClick={() => setMode('own')}
              className="group text-left bg-white border border-[#E5E2DC] overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
            >
              <div className="h-80 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1626256223743-603107306ff2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxoYW5kJTIwc2tldGNoaW5nJTIwcHJvZHVjdCUyMGRlc2lnbiUyMG5vdGVib29rJTIwb3ZlcmhlYWR8ZW58MHx8fHwxNzc2NDkxNDU1fDA&ixlib=rb-4.1.0&q=85"
                  alt="Bulk order your own design"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-light text-[#641b4d] mb-2">Bulk Order Your Own Design</h3>
                <p className="text-sm text-[#7a5e75]">Have a custom design? Share it and we'll manufacture in bulk at competitive rates.</p>
              </div>
            </button>
          </motion.div>
        )}

        {/* Step 2a: Existing Design - Select Product */}
        {mode === 'existing' && !selectedProduct && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button data-testid="back-to-bulk-options" onClick={() => setMode(null)} className="flex items-center gap-2 text-sm text-[#7a5e75] hover:text-[#641b4d] mb-8 transition-colors">
              <ArrowLeft size={20} /> Back to options
            </button>
            <h2 className="text-2xl font-light mb-8">Select a design for bulk order</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map(product => (
                <button key={product.id} data-testid={`bulk-select-product-${product.id}`} onClick={() => setSelectedProduct(product)} className="group text-left bg-white border border-[#E5E2DC] overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                  <div className="overflow-hidden aspect-square bg-[#F4F4F4]">
                    <img src={product.image_url} alt={product.name} className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-light text-[#641b4d]">{product.name}</h3>
                    {product.shape && <p className="text-xs text-[#7a5e75] mt-1">{product.shape}</p>}
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Step 2b: Existing Design - Order Form */}
        {mode === 'existing' && selectedProduct && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button data-testid="back-to-bulk-products" onClick={() => setSelectedProduct(null)} className="flex items-center gap-2 text-sm text-[#7a5e75] hover:text-[#641b4d] mb-8 transition-colors">
              <ArrowLeft size={20} /> Back to designs
            </button>

            <div className="bg-white border border-[#E5E2DC] p-6 mb-8 flex gap-6 items-center">
              <img src={selectedProduct.image_url} alt={selectedProduct.name} className="w-24 h-24 object-contain bg-[#F4F4F4]" />
              <div>
                <h3 className="text-xl font-light mb-1">{selectedProduct.name}</h3>
                {selectedProduct.shape && <p className="text-sm text-[#7a5e75]">{selectedProduct.shape}</p>}
                <p className="text-lg font-semibold text-[#641b4d] mt-2">{selectedProduct.price?.toFixed(2) ? `₹${selectedProduct.price.toFixed(2)} per unit` : ''}</p>
              </div>
            </div>

            <div className="bg-white border border-[#E5E2DC] p-8">
              <h2 className="text-2xl font-light mb-6">Bulk Order Details</h2>
              <form onSubmit={handleExistingSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Full Name *</label>
                    <input type="text" data-testid="bulk-existing-name" value={existingForm.name} onChange={e => setExistingForm({...existingForm, name: e.target.value})} required className="input-underline w-full" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Email *</label>
                    <input type="email" data-testid="bulk-existing-email" value={existingForm.email} onChange={e => setExistingForm({...existingForm, email: e.target.value})} required className="input-underline w-full" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Phone *</label>
                    <input type="tel" data-testid="bulk-existing-phone" value={existingForm.phone} onChange={e => setExistingForm({...existingForm, phone: e.target.value})} required className="input-underline w-full" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Quantity (Min 10) *</label>
                    <input type="number" data-testid="bulk-existing-quantity" value={existingForm.quantity} onChange={e => setExistingForm({...existingForm, quantity: e.target.value})} required min={10} className="input-underline w-full" />
                  </div>
                </div>

                {/* Color Options (mirrors product-detail page) */}
                {selectedProduct.colors && selectedProduct.colors.length > 0 && (
                  <div>
                    <span className="text-xs uppercase tracking-widest text-[#7a5e75] mb-3 block">Color *</span>
                    <div className="flex flex-wrap gap-2">
                      {selectedProduct.colors.map(color => (
                        <button
                          key={color}
                          type="button"
                          data-testid={`bulk-color-${color.toLowerCase().replace(/\s+/g, '-')}`}
                          onClick={() => setExistingForm({...existingForm, color})}
                          className={`px-4 py-2 text-xs uppercase tracking-wider border transition-all ${
                            existingForm.color === color
                              ? 'bg-[#641b4d] text-white border-[#641b4d]'
                              : 'bg-white text-[#641b4d] border-[#E5E2DC] hover:border-[#641b4d]'
                          }`}
                        >
                          {color}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Finish Options (mirrors product-detail page) */}
                <div>
                  <span className="text-xs uppercase tracking-widest text-[#7a5e75] mb-3 block">Finish *</span>
                  <div className="flex flex-wrap gap-2">
                    {finishOptions.map(finish => (
                      <button
                        key={finish}
                        type="button"
                        data-testid={`bulk-finish-${finish.toLowerCase()}`}
                        onClick={() => setExistingForm({...existingForm, finish})}
                        className={`px-4 py-2 text-xs uppercase tracking-wider border transition-all ${
                          existingForm.finish === finish
                            ? 'bg-[#641b4d] text-white border-[#641b4d]'
                            : 'bg-white text-[#641b4d] border-[#E5E2DC] hover:border-[#641b4d]'
                        }`}
                      >
                        {finish}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Additional Message</label>
                  <textarea data-testid="bulk-existing-message" rows={4} placeholder="Any special requirements or questions?" value={existingForm.message} onChange={e => setExistingForm({...existingForm, message: e.target.value})} className="input-underline w-full resize-none" />
                </div>

                <div data-testid="bulk-delivery-note" className="bg-[#F4F4F4] border border-[#E5E2DC] px-6 py-4">
                  <p className="text-sm text-[#7a5e75]">
                    Bulk orders require additional production time. The estimated delivery date and final pricing will be confirmed once your order is reviewed by our team.
                  </p>
                </div>

                <button type="submit" data-testid="submit-bulk-existing" disabled={loading} className="btn-primary w-full disabled:opacity-50">
                  {loading ? 'Submitting...' : 'Submit Bulk Inquiry'}
                </button>
              </form>
            </div>
          </motion.div>
        )}

        {/* Step 2c: Own Design */}
        {mode === 'own' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button data-testid="back-to-bulk-options-own" onClick={() => setMode(null)} className="flex items-center gap-2 text-sm text-[#7a5e75] hover:text-[#641b4d] mb-8 transition-colors">
              <ArrowLeft size={20} /> Back to options
            </button>

            <div className="bg-white border border-[#E5E2DC] p-8">
              <h2 className="text-2xl font-light mb-6">Bulk Order — Your Design</h2>
              <form onSubmit={handleOwnSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Full Name *</label>
                    <input type="text" data-testid="bulk-own-name" value={ownForm.name} onChange={e => setOwnForm({...ownForm, name: e.target.value})} required className="input-underline w-full" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Email *</label>
                    <input type="email" data-testid="bulk-own-email" value={ownForm.email} onChange={e => setOwnForm({...ownForm, email: e.target.value})} required className="input-underline w-full" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Phone *</label>
                    <input type="tel" data-testid="bulk-own-phone" value={ownForm.phone} onChange={e => setOwnForm({...ownForm, phone: e.target.value})} required className="input-underline w-full" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Quantity (Min 10) *</label>
                    <input type="number" data-testid="bulk-own-quantity" value={ownForm.quantity} onChange={e => setOwnForm({...ownForm, quantity: e.target.value})} required min={10} className="input-underline w-full" />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Design Requirements *</label>
                  <textarea data-testid="bulk-own-requirements" value={ownForm.requirements} onChange={e => setOwnForm({...ownForm, requirements: e.target.value})} required rows={6} placeholder="Please describe your design (dimensions, shape, color, finish, quantity, etc.)" className="input-underline w-full resize-none" />
                </div>

                {/* File Upload */}
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#7a5e75] mb-3 block">Upload Your Design</label>
                  <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".jpg,.jpeg,.png,.pdf" className="hidden" />
                  {!uploadedFile ? (
                    <button type="button" data-testid="bulk-upload-design" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="w-full border-2 border-dashed border-[#E5E2DC] hover:border-[#7a5e75] transition-colors py-8 flex flex-col items-center gap-3">
                      <UploadSimple size={32} weight="light" className="text-[#7a5e75]" />
                      <span className="text-sm text-[#7a5e75]">{uploading ? 'Uploading...' : 'Click to upload JPEG, PNG, or PDF'}</span>
                    </button>
                  ) : (
                    <div data-testid="bulk-uploaded-file" className="flex items-center justify-between bg-[#F4F4F4] border border-[#E5E2DC] px-6 py-4">
                      <div className="flex items-center gap-3">
                        <FileIcon size={24} weight="light" className="text-[#641b4d]" />
                        <span className="text-sm text-[#641b4d]">{uploadedFile.filename}</span>
                      </div>
                      <button type="button" onClick={() => { setUploadedFile(null); fileInputRef.current.value = ''; }} className="text-[#bc64aa] hover:text-[#8c1666]">
                        <X size={20} />
                      </button>
                    </div>
                  )}
                </div>

                <div data-testid="bulk-own-delivery-note" className="bg-[#F4F4F4] border border-[#E5E2DC] px-6 py-4">
                  <p className="text-sm text-[#7a5e75]">
                    Custom bulk orders require additional production time. The estimated delivery date and final pricing will be confirmed once your order is reviewed by our team.
                  </p>
                </div>

                <button type="submit" data-testid="submit-bulk-own" disabled={loading} className="btn-primary w-full disabled:opacity-50">
                  {loading ? 'Submitting...' : 'Submit Bulk Inquiry'}
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BulkInquiry;
